import { useState } from 'react';
import { Link } from 'react-router';

import { CycleStage, type CycleStandingResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  PageHeader,
  Panel,
  TextField,
  type Column,
} from '../../components';
import { formatDate, formatDateTime, formatInteger } from '../../format';
import {
  staffSubmissionConsolidatedPath,
  staffSubmissionContractPath,
  staffSubmissionGroupPath,
} from '../../routes/paths';
import { useSubmissionCalendar } from '../queries';
import {
  STAGE_LABELS,
  SUBMISSION_COPY,
  approvedOf,
  calendarLines,
  currentCycleMonth,
  type CalendarLine,
} from './submissionCycle';

/**
 * The monthly rhythm: what has gone out, what has not, and the proof.
 *
 * The screen after the review queue. There, movements are decided and the
 * cutoff is signed off; here the file that sign-off pinned is packaged, filed
 * and marked as filed. The two are separate screens rather than one long page
 * because they are separate jobs done at separate moments — the queue is read
 * all month and this is worked through at the cutoff — and because the question
 * this one answers is a single one: **what still has to go out?**
 *
 * That question is the calendar, and `outstanding_count` is the number it
 * exists to drive to zero. `overdue_count` is how many of those are already
 * late by our own calendar — our cutoff has passed, which is the only
 * deadline this product actually knows about. The insurer's own deadline is not
 * something the contract records, so the screen does not pretend to.
 *
 * One contract's month is a page of its own rather than a panel underneath
 * the list, because the list is long: an analyst working through it used to
 * scroll between the line and whatever the last "Abrir" had opened below,
 * which is how the wrong line gets worked on. "Abrir" navigates now, and the
 * address carries the contract — so it can be bookmarked, reloaded and pasted
 * to a colleague.
 *
 * **Nothing on this screen transmits anything to an insurer**, and it says so
 * where an analyst will read it rather than only in the API documentation. What
 * that provider accepts by way of an automated feed is an open question;
 * everything up to the handover is here, and the handover is a person whose
 * work is recorded.
 */
export function SubmissionsPage() {
  const [cycleMonth, setCycleMonth] = useState(currentCycleMonth());

  const calendar = useSubmissionCalendar(cycleMonth);

  const columns: readonly Column<CalendarLine>[] = [
    {
      key: 'company',
      header: 'Cliente',
      render: (line) =>
        line.kind === 'group' ? (
          <>
            {SUBMISSION_COPY.groupLabel} {lead(line).group_name}
            <br />
            <span className="table__muted">{companiesOf(line.members)}</span>
          </>
        ) : (
          lead(line).company_name
        ),
    },
    {
      key: 'insurer',
      header: 'Operadora',
      render: (line) => (
        <>
          {lead(line).insurer_name}
          <br />
          <span className="table__muted">
            {line.kind === 'contract'
              ? `${line.standing.subestipulante} / ${line.standing.modulo}`
              : `${line.kind === 'group' ? SUBMISSION_COPY.groupTitle : SUBMISSION_COPY.consolidatedTitle} · ${line.members.length} contratos · ${approvedOf(line.approvedCount, line.members.length)}`}
          </span>
        </>
      ),
    },
    { key: 'cutoff', header: 'Corte', render: (line) => formatDate(lead(line).cutoff_date) },
    {
      key: 'stage',
      header: 'Situação',
      render: (line) => (
        <StageBadge stage={line.kind === 'contract' ? line.standing.stage : line.stage} />
      ),
    },
    {
      key: 'overdue',
      header: 'Prazo',
      render: (line) =>
        isOverdue(line) ? (
          <span className="badge badge--warning">{SUBMISSION_COPY.overdue}</span>
        ) : (
          <span className="table__muted">—</span>
        ),
    },
    {
      key: 'sent',
      header: 'Enviado em',
      render: (line) => {
        const sentAt = lead(line).submission?.submitted_at;
        return sentAt != null ? formatDateTime(sentAt) : <span className="table__muted">—</span>;
      },
    },
    {
      key: 'open',
      header: 'Ações',
      align: 'end',
      render: (line) => (
        <Link
          className="button button--secondary"
          to={
            line.kind === 'contract'
              ? staffSubmissionContractPath(line.standing.contract_id, cycleMonth)
              : line.kind === 'group'
                ? staffSubmissionGroupPath(
                    lead(line).group_id ?? '',
                    lead(line).insurer_id,
                    cycleMonth,
                  )
                : staffSubmissionConsolidatedPath(
                    lead(line).company_id,
                    lead(line).insurer_id,
                    cycleMonth,
                  )
          }
        >
          {SUBMISSION_COPY.open}
        </Link>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader title={SUBMISSION_COPY.title} description={SUBMISSION_COPY.description} />

      <Panel
        title={SUBMISSION_COPY.calendarTitle}
        description={SUBMISSION_COPY.calendarDescription}
      >
        <TextField
          label={SUBMISSION_COPY.cycleLabel}
          hint={SUBMISSION_COPY.cycleHint}
          type="month"
          value={cycleMonth}
          onChange={setCycleMonth}
        />
        <AsyncSection
          state={calendar}
          loadingLabel="Carregando os envios…"
          errorTitle="Não foi possível carregar os envios"
          isEmpty={(data) => data.items.length === 0}
          emptyTitle="Nenhum contrato"
          emptyDescription={SUBMISSION_COPY.noContracts}
        >
          {(data) => (
            <>
              <dl className="definitions">
                <div>
                  <dt>{SUBMISSION_COPY.outstanding}</dt>
                  <dd>{formatInteger(data.outstanding_count)}</dd>
                </div>
                <div>
                  <dt>{SUBMISSION_COPY.overdue}</dt>
                  <dd>{formatInteger(data.overdue_count)}</dd>
                </div>
              </dl>
              <DataTable
                id="submissions"
                caption="Contratos e o envio deste ciclo"
                columns={columns}
                rows={calendarLines(data.items)}
                rowKey={(line) => line.key}
                emptyTitle="Nenhum contrato"
              />
            </>
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/**
 * The stage, coloured by whether it still needs something from us.
 *
 * `sent` is the only closed state, so it is the only quiet one. A refusal is
 * loud because it is the one stage that needs a person today.
 */
function StageBadge({ stage }: { stage: CycleStage }) {
  const tone =
    stage === CycleStage.SENT
      ? 'badge--ok'
      : stage === CycleStage.FAILED
        ? 'badge--blocking'
        : 'badge--warning';
  return <span className={`badge ${tone}`}>{STAGE_LABELS[stage]}</span>;
}

/**
 * The member a line is read from for what every member shares: the company
 * (or the group), the insurer, the cutoff. A single file's members are one
 * company -- or one group -- with one insurer by construction, so the first of
 * them speaks for all.
 */
function lead(line: CalendarLine): CycleStandingResponse {
  return line.kind === 'contract' ? line.standing : (line.members[0] as CycleStandingResponse);
}

/** The group's companies on a line, each once, in the order they appear. */
function companiesOf(members: readonly CycleStandingResponse[]): string {
  return [...new Set(members.map((member) => member.company_name))].join(', ');
}

/** Late by our own calendar: any member past its cutoff, and the month not sent. */
function isOverdue(line: CalendarLine): boolean {
  if (line.kind === 'contract') {
    return line.standing.is_overdue;
  }
  return line.stage !== CycleStage.SENT && line.members.some((member) => member.is_past_cutoff);
}
